import { motion } from "framer-motion";
import { Check, Zap, Users, BarChart3, MessageSquare, BrainCircuit, Shield } from "lucide-react";

const benefits = [
  "Unlimited realistic practice scenarios",
  "AI adapts to your skill level in real-time",
  "Instant performance scoring and coaching",
  "Scale training across entire teams",
  "Track measurable improvement over time",
  "Available 24/7 — no scheduling needed",
];

export default function SolutionSection() {
  return (
    <section id="solution" data-testid="solution-section" className="py-[45px] md:py-[90px] stripe-bg-white relative">
      <div className="absolute inset-0 section-glow-right" />
      <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Left - Visual */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative order-2 lg:order-1"
          >
            <div className="stripe-card rounded-2xl p-6 relative overflow-hidden">
              {/* Mock UI */}
              <div className="flex items-center gap-2 mb-4 pb-4 border-b border-stripe-border">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-yellow-400" />
                <div className="w-3 h-3 rounded-full bg-emerald-400" />
                <span className="ml-4 text-xs text-stripe-text-muted font-mono">skillsimai.app/simulator</span>
              </div>

              <div className="flex gap-4">
                {/* Sidebar */}
                <div className="hidden sm:block w-40 shrink-0">
                  <div className="space-y-2">
                    {[
                      { icon: MessageSquare, label: "Scenarios", active: true },
                      { icon: BarChart3, label: "Analytics", active: false },
                      { icon: Users, label: "Team", active: false },
                      { icon: BrainCircuit, label: "Coaching", active: false },
                      { icon: Shield, label: "Settings", active: false },
                    ].map((item, i) => (
                      <div
                        key={i}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${
                          item.active ? "bg-stripe-primary/10 text-stripe-primary font-medium" : "text-stripe-text-muted"
                        }`}
                      >
                        <item.icon className="w-3.5 h-3.5" />
                        {item.label}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Main content mock */}
                <div className="flex-1 space-y-3">
                  <div className="bg-slate-50 rounded-xl p-4 border border-stripe-border">
                    <div className="text-xs text-stripe-text-muted mb-1">Active Scenario</div>
                    <div className="text-sm font-medium text-navy">Sales Objection Handling</div>
                    <div className="flex gap-2 mt-3">
                      <span className="px-2 py-0.5 bg-stripe-primary/10 text-stripe-primary rounded-full text-[10px] font-medium">Advanced</span>
                      <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded-full text-[10px] font-medium">In Progress</span>
                    </div>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4 border border-stripe-border">
                    <div className="text-xs text-stripe-text-muted mb-2">Current Score</div>
                    <div className="flex items-end gap-1">
                      {[40, 55, 65, 70, 78, 82, 88].map((h, i) => (
                        <div
                          key={i}
                          className="w-6 rounded-t relative"
                          style={{ height: `${h * 0.6}px`, background: `rgba(99,91,255,${0.15 + (i/7)*0.6})` }}
                        >
                          {i === 6 && (
                            <div className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] font-bold text-stripe-primary">
                              88
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating element */}
            <div className="absolute -bottom-4 -right-4 bg-emerald-500 text-white rounded-xl px-4 py-3 shadow-lg shadow-emerald-500/20">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4" />
                <span className="text-sm font-medium">+34% improvement</span>
              </div>
            </div>
          </motion.div>

          {/* Right - Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="order-1 lg:order-2"
          >
            <div className="section-overline mb-4">The Solution</div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mb-6">
              Train soft skills like an <span className="bg-gradient-to-r from-stripe-primary to-cyan-400 bg-clip-text text-transparent">athlete</span> trains <span className="bg-gradient-to-r from-stripe-primary to-cyan-400 bg-clip-text text-transparent">muscles</span>
            </h2>
            <p className="text-base md:text-lg text-stripe-text-secondary leading-relaxed mb-10">
              SkillsimAI is the AI-powered simulator that gives your team unlimited practice in
              negotiation, sales, and difficult conversations — with real-time feedback and
              measurable results.
            </p>

            <div className="space-y-4">
              {benefits.map((b, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  className="flex items-center gap-3"
                >
                  <div className="w-6 h-6 rounded-full bg-emerald-50 flex items-center justify-center shrink-0 border border-emerald-100">
                    <Check className="w-3.5 h-3.5 text-emerald-600" />
                  </div>
                  <span className="text-sm text-stripe-text-secondary">{b}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
